import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchClienteProcessos } from '@/api/clientes';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { formatNumeroProcesso } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface Base {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ocupado: boolean;
}

/** "Retorno feito", com uma observação opcional — interna, não vai ao cliente. */
export function RetornoDialog({ open, onOpenChange, ocupado, onConfirmar }: Base & { onConfirmar: (observacao: string) => void }) {
  const [observacao, setObservacao] = useState('');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar o retorno ao cliente</DialogTitle>
          <DialogDescription>
            O atendimento sai de “Aguardando você” e vai para “Em preparação”. Ao cliente que perguntar, o Secretário diz que você já
            entrou em contato.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Observação, só para você (opcional). Ex.: liguei, vamos protocolar semana que vem."
          maxLength={500}
          rows={3}
          aria-label="Observação do retorno"
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => onConfirmar(observacao.trim())} disabled={ocupado}>
            {ocupado && <Spinner className="mr-2 text-inherit" />}
            Retorno feito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MOTIVOS = ['Não há caso', 'Cliente desistiu', 'Cliente não respondeu ao retorno', 'Tratado fora do sistema'];

/** "Encerrar", com motivo — é o que o Secretário repete ao cliente, então é dito assim. */
export function EncerrarDialog({ open, onOpenChange, ocupado, onConfirmar }: Base & { onConfirmar: (motivo: string) => void }) {
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [outro, setOutro] = useState('');
  const motivo = escolhido === 'outro' ? outro.trim() : (escolhido ?? '');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Encerrar o atendimento</DialogTitle>
          <DialogDescription>
            Sem processo. O motivo é o que o Secretário diz ao cliente que perguntar do caso, então escreva para ele ler.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Motivo do encerramento">
          {MOTIVOS.map((m) => (
            <Button
              key={m}
              type="button"
              variant={escolhido === m ? 'default' : 'outline'}
              className="min-h-11 justify-start"
              role="radio"
              aria-checked={escolhido === m}
              onClick={() => setEscolhido(m)}
            >
              {m}
            </Button>
          ))}
          <Button
            type="button"
            variant={escolhido === 'outro' ? 'default' : 'outline'}
            className="min-h-11 justify-start"
            role="radio"
            aria-checked={escolhido === 'outro'}
            onClick={() => setEscolhido('outro')}
          >
            Outro motivo
          </Button>
          {escolhido === 'outro' && (
            <Textarea
              value={outro}
              onChange={(e) => setOutro(e.target.value)}
              placeholder="Em uma frase, como o cliente deve ouvir."
              maxLength={300}
              rows={2}
              aria-label="Outro motivo"
            />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => onConfirmar(motivo)} disabled={ocupado || motivo === ''}>
            {ocupado && <Spinner className="mr-2 text-inherit" />}
            Encerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Vincular processo": escolhe um processo do cliente. Nunca inferido pela data. */
export function VincularProcessoDialog({
  open,
  onOpenChange,
  ocupado,
  clienteId,
  onConfirmar,
}: Base & { clienteId: string | null; onConfirmar: (processoId: string) => void }) {
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const processos = useQuery({
    queryKey: queryKeys.clientes.processos(clienteId ?? ''),
    queryFn: () => fetchClienteProcessos(clienteId!),
    enabled: open && !!clienteId,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vincular a um processo</DialogTitle>
          <DialogDescription>
            O atendimento sai de “Em preparação” e o Secretário passa a responder com o andamento desse processo.
          </DialogDescription>
        </DialogHeader>
        {!clienteId ? (
          <p className="text-sm text-muted-foreground">Este atendimento não tem cliente cadastrado.</p>
        ) : processos.isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size={32} label="Carregando processos" />
          </div>
        ) : (processos.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum processo cadastrado para este cliente. Cadastre o processo na{' '}
            <Link to={`/app/clientes/${clienteId}`} className="underline">
              pasta do cliente
            </Link>{' '}
            e volte aqui.
          </p>
        ) : (
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Processo">
            {(processos.data ?? []).map((p) => (
              <Button
                key={p.id}
                type="button"
                variant={escolhido === p.id ? 'default' : 'outline'}
                className="h-auto min-h-11 justify-start whitespace-normal py-2 text-left"
                role="radio"
                aria-checked={escolhido === p.id}
                onClick={() => setEscolhido(p.id)}
              >
                <span className="min-w-0 break-words">
                  {formatNumeroProcesso(p.numero)} · {p.classe} · {p.tribunal}
                </span>
              </Button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => escolhido && onConfirmar(escolhido)} disabled={ocupado || !escolhido}>
            {ocupado && <Spinner className="mr-2 text-inherit" />}
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
